// Auth audit log (/v1/audit and /v1/teams/{id}/audit). Read-only, keyset-
// paginated on `created`: pass the previous page's `nextBefore` as `before` to
// fetch the next (older) page. The event `type` vocabulary is owned by the Go
// constants in internal/audit; typeLabel maps each type to human copy and
// deliberately falls back to the raw string so a new server-side event type
// shows up verbatim instead of being hidden or mislabeled.

import { getJSON, query } from "./http.js";

/**
 * AuditEvent is one audit row. ip/userAgent/teamId may be empty: model-hook
 * emissions (e.g. member changes through auto-REST) carry no request context,
 * and account-scoped events have no team.
 */
export interface AuditEvent {
  id: string;
  type: string;
  ip: string;
  userAgent: string;
  meta: Record<string, unknown> | null;
  created: string;
  userId: string;
  teamId: string;
}

/** AuditPage is one page of events (newest first) plus the older-page cursor. */
export interface AuditPage {
  items: AuditEvent[];
  /** `before` value for the next (older) page; empty when exhausted. */
  nextBefore: string;
}

export interface AuditListOpts {
  /** Only events created strictly before this cursor (from a prior page). */
  before?: string;
  /** Page size; the server caps it at 100. */
  limit?: number;
}

/** listOwnAudit returns the caller's own recent auth events. */
export function listOwnAudit(opts: AuditListOpts = {}): Promise<AuditPage> {
  return getJSON<AuditPage>(`/v1/audit${query({ before: opts.before, limit: opts.limit })}`);
}

/** listTeamAudit returns a team's events (maintainer+; the server 403s below that). */
export function listTeamAudit(teamId: string, opts: AuditListOpts = {}): Promise<AuditPage> {
  return getJSON<AuditPage>(
    `/v1/teams/${encodeURIComponent(teamId)}/audit${query({ before: opts.before, limit: opts.limit })}`,
  );
}

// Human copy for the closed event vocabulary (Go constants in internal/audit).
const TYPE_LABELS: Record<string, string> = {
  login_otp: "Signed in with email code",
  login_passkey: "Signed in with passkey",
  login_oauth2: "Signed in with SSO",
  login_failed: "Failed sign-in attempt",
  otp_requested: "Email code requested",
  stepup_verified: "Identity verified",
  apikey_created: "API key created",
  apikey_revoked: "API key revoked",
  apikey_exchanged: "API key exchanged for a short-lived token",
  passkey_added: "Passkey added",
  passkey_removed: "Passkey removed",
  member_added: "Team member added",
  member_removed: "Team member removed",
  member_role_changed: "Team member role changed",
  invite_created: "Invitation sent",
  invite_accepted: "Invitation accepted",
  invite_revoked: "Invitation revoked",
  signout_all: "Signed out everywhere",
  team_deleted: "Team deleted",
  account_deleted: "Account deleted",
  account_exported: "Account data exported",
  repository_pushed: "Pushed to a repository",
  repository_exported: "Repository exported",
  data_sealed: "Data drop sealed",
  data_source_changed: "Data source changed",
  data_pulled: "Data pulled",
  workspace_pin_lifted: "Workspace pin lifted",
};

/** typeLabel turns an event type into human copy (the raw type when unknown). */
export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}
